/**
 * YourTierPanel — read-only "Your tier" readout for the Partner Profile page.
 *
 * SOURCE OF TRUTH:
 *   Both the displayed commission % AND the upgrade picker pull from the
 *   live `partner_tiers` registry via `GET /api/profiles/provider/tiers`
 *   (the partner-gated mirror of `/api/admin/partner-tiers`). The exact
 *   same `commission_pct` field is consumed by the pricing engine via
 *   `_resolve_tier_commission_pct()` in bookings.py — so what the partner
 *   sees here is what gets booked. There is NO hardcoded client-side
 *   tier table; if `partner_tiers.standard.commission_pct` is admin-edited
 *   to 17.5, this panel surfaces 17.5 on the next mount.
 *
 * READ-ONLY:
 *   The panel has zero affordance to mutate the partner's own tier_id.
 *   The "Request upgrade" CTA only POSTs to
 *   `/api/profiles/provider/tier-upgrade-request` which creates a doc
 *   with status="pending". Admin approval (via the existing
 *   admin queue → `POST /api/admin/tier-upgrade-requests/{id}/approve`)
 *   is what reassigns the tier. No auto-approval path exists.
 *
 * PENDING STATE:
 *   If a pending request already exists, the CTA is replaced with a
 *   "Pending review" pill showing the requested tier name + submitted
 *   timestamp. (Cancel-own-request is OUT OF SCOPE this dispatch.)
 *
 * TOP-TIER STATE:
 *   If the partner is on the lowest sort_order tier (the highest rank
 *   per the Phase 3 / Section F sort convention), the CTA is replaced
 *   with a thank-you message — there is no upgrade path.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Award, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

function fmtPct(v) {
  if (v === null || v === undefined) return "—";
  return `${Number(v).toFixed(Number.isInteger(Number(v)) ? 0 : 1)}%`;
}
function fmtMonthly(v) {
  // Only render the $/mo line when monthly_price is a positive number.
  // Free tiers (Standard at $0/mo today) get no price line.
  if (v === null || v === undefined) return null;
  const n = Number(v);
  if (!Number.isFinite(n) || n <= 0) return null;
  return `$${n % 1 === 0 ? n.toFixed(0) : n.toFixed(2)}/mo`;
}
function fmtTimestamp(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "";
  }
}

function fmtDollarsFromCents(cents) {
  if (cents === null || cents === undefined) return null;
  const n = Number(cents) / 100;
  return `$${n % 1 === 0 ? n.toFixed(0) : n.toFixed(2)}`;
}

/**
 * UpgradeEstimateLine — the "what you'd save at the next tier" line.
 *
 * Renders the four branches described in the spec:
 *   1. has_sufficient_history === false   → neutral fallback (no $).
 *   2. next_tier_id === null              → suppressed (Elite); caller's
 *                                            "top tier" message covers it.
 *   3. net_cents > 0                      → encouraging copy + $/mo
 *                                            after the $X/mo plan tail.
 *   4. net_cents <= 0                     → honest "wouldn't pay off yet"
 *                                            + break-even gap.
 *
 * ZERO tier-rate arithmetic happens here. Every number rendered is a
 * field on the API response from
 * `GET /api/profiles/provider/tier-upgrade-estimate`.
 */
function UpgradeEstimateLine({ estimate }) {
  if (estimate === null || estimate === undefined) return null;
  // Elite path — caller renders the "top tier" message; suppress here.
  if (estimate.next_tier_id === null || estimate.next_tier_id === undefined)
    return null;
  // Insufficient history — neutral fallback, no $ figure.
  if (!estimate.has_sufficient_history) {
    return (
      <p
        className="mt-3 text-sm text-muted-foreground"
        data-testid="your-tier-estimate-insufficient"
      >
        Not enough booking history yet to estimate what an upgrade would
        net you.
      </p>
    );
  }
  const monthlyPriceLabel = fmtDollarsFromCents(estimate.next_tier_monthly_price_cents);
  if (estimate.net_cents > 0) {
    return (
      <p
        className="mt-3 text-sm text-pp-text"
        data-testid="your-tier-estimate-positive"
      >
        At your recent volume,{" "}
        <span className="font-semibold">{estimate.next_tier_name}</span>{" "}
        would net you about{" "}
        <span
          className="font-semibold text-brand-orange"
          data-testid="your-tier-estimate-net"
        >
          {fmtDollarsFromCents(estimate.net_cents)}/mo
        </span>
        {estimate.next_tier_monthly_price_cents > 0 ? (
          <>
            {" "}after the{" "}
            <span data-testid="your-tier-estimate-monthly-cost">
              {monthlyPriceLabel}/mo
            </span>{" "}
            plan.
          </>
        ) : (
          "."
        )}
      </p>
    );
  }
  // net_cents <= 0 — honest "wouldn't pay off yet" branch.
  return (
    <p
      className="mt-3 text-sm text-muted-foreground"
      data-testid="your-tier-estimate-negative"
    >
      At your current volume, upgrading to{" "}
      <span className="font-semibold text-pp-text">
        {estimate.next_tier_name}
      </span>{" "}
      wouldn’t pay off yet — you’d need about{" "}
      <span
        className="font-semibold text-pp-text"
        data-testid="your-tier-estimate-gap"
      >
        {fmtDollarsFromCents(estimate.break_even_gap_cents)}/mo more
      </span>{" "}
      in commissionable booking volume to come out ahead of the{" "}
      <span data-testid="your-tier-estimate-monthly-cost">
        {monthlyPriceLabel}/mo
      </span>{" "}
      plan.
    </p>
  );
}

export default function YourTierPanel({ currentTierId }) {
  const [tiers, setTiers] = useState(null); // null = loading, [] = empty
  const [requests, setRequests] = useState(null);
  const [estimate, setEstimate] = useState(null); // null = loading, {} = data, false = errored
  const [pickerOpen, setPickerOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // ---- Initial load ------------------------------------------------------
  const loadAll = async () => {
    try {
      const [tiersResp, reqsResp, estResp] = await Promise.all([
        api.get("/profiles/provider/tiers"),
        api.get("/profiles/provider/tier-upgrade-requests"),
        api.get("/profiles/provider/tier-upgrade-estimate"),
      ]);
      setTiers(tiersResp.data || []);
      setRequests(reqsResp.data || []);
      setEstimate(estResp.data || null);
      setError(null);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [tiersResp, reqsResp, estResp] = await Promise.all([
          api.get("/profiles/provider/tiers"),
          api.get("/profiles/provider/tier-upgrade-requests"),
          api.get("/profiles/provider/tier-upgrade-estimate"),
        ]);
        if (!active) return;
        setTiers(tiersResp.data || []);
        setRequests(reqsResp.data || []);
        setEstimate(estResp.data || null);
      } catch (e) {
        if (active) {
          // Estimate is opportunistic — if it fails, fall back to false
          // so the panel still renders tier name + commission. Don't surface
          // an error toast that blocks the whole page.
          setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  // ---- Derived state -----------------------------------------------------
  const sortedTiers = useMemo(() => {
    if (!Array.isArray(tiers)) return [];
    // sort_order ASCENDING (per Phase 3 / Section F: lower = higher rank).
    return [...tiers].sort(
      (a, b) => (a.sort_order ?? 999) - (b.sort_order ?? 999),
    );
  }, [tiers]);

  const currentTier = useMemo(() => {
    if (!currentTierId || !sortedTiers.length) return null;
    return sortedTiers.find((t) => t.id === currentTierId) || null;
  }, [currentTierId, sortedTiers]);

  const upgradeOptions = useMemo(() => {
    if (!currentTier || !sortedTiers.length) return [];
    // Tiers with a STRICTLY LOWER sort_order than the partner's current
    // tier (higher rank = upgrade path). Excludes the current tier itself.
    return sortedTiers.filter(
      (t) => (t.sort_order ?? 999) < (currentTier.sort_order ?? 999),
    );
  }, [currentTier, sortedTiers]);

  const pendingRequest = useMemo(() => {
    if (!Array.isArray(requests)) return null;
    return requests.find((r) => r.status === "pending") || null;
  }, [requests]);

  const pendingTierName = useMemo(() => {
    if (!pendingRequest) return null;
    const t = sortedTiers.find((x) => x.id === pendingRequest.requested_tier_id);
    return t ? t.name : pendingRequest.requested_tier_id;
  }, [pendingRequest, sortedTiers]);

  // ---- Actions -----------------------------------------------------------
  const submitUpgrade = async (requestedTierId) => {
    setSubmitting(true);
    try {
      await api.post("/profiles/provider/tier-upgrade-request", {
        requested_tier_id: requestedTierId,
      });
      toast.success("Upgrade request submitted — awaiting admin review.");
      setPickerOpen(false);
      await loadAll();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || err.message,
      );
    } finally {
      setSubmitting(false);
    }
  };

  // ---- Render ------------------------------------------------------------
  const loading = tiers === null || requests === null;

  return (
    <Card
      className="mt-6 rounded-3xl border-border bg-pp-surface p-8 shadow-none"
      data-testid="your-tier-panel"
    >
      <header className="mb-4 flex items-center gap-2">
        <Award className="h-5 w-5 text-brand-orange" />
        <h3 className="font-heading text-xl font-bold text-pp-text">
          Your tier
        </h3>
      </header>

      {loading && !error && (
        <p
          className="text-sm text-muted-foreground"
          data-testid="your-tier-loading"
        >
          Loading tier…
        </p>
      )}

      {error && (
        <p
          className="text-sm text-destructive"
          data-testid="your-tier-error"
        >
          Could not load tier — {error}
        </p>
      )}

      {!loading && !error && !currentTier && (
        <p
          className="text-sm text-muted-foreground"
          data-testid="your-tier-unassigned"
        >
          Your account hasn’t been assigned a tier yet — an admin will set
          one shortly.
        </p>
      )}

      {!loading && !error && currentTier && (
        <>
          {/* Current tier readout */}
          <div
            className="flex flex-wrap items-baseline gap-x-4 gap-y-2"
            data-testid="your-tier-current"
          >
            <span
              className="font-heading text-2xl font-bold text-pp-text"
              data-testid="your-tier-name"
            >
              {currentTier.name}
            </span>
            <span
              className="text-sm text-muted-foreground"
              data-testid="your-tier-commission"
            >
              {fmtPct(currentTier.commission_pct)} commission
            </span>
            {fmtMonthly(currentTier.monthly_price) !== null && (
              <span
                className="text-sm text-muted-foreground"
                data-testid="your-tier-monthly"
              >
                · {fmtMonthly(currentTier.monthly_price)}
              </span>
            )}
          </div>

          <p className="mt-1 text-xs text-muted-foreground">
            This is the commission applied to every new booking. Existing
            bookings keep their original snapshot.
          </p>

          <UpgradeEstimateLine estimate={estimate} />

          {/* CTA / pending / top-tier states */}
          <div className="mt-5">
            {pendingRequest ? (
              <div
                className="flex flex-wrap items-center gap-3 rounded-2xl border border-border bg-muted px-4 py-3"
                data-testid="your-tier-pending"
              >
                <Badge
                  variant="secondary"
                  className="rounded-full"
                  data-testid="your-tier-pending-badge"
                >
                  Pending review
                </Badge>
                <span className="text-sm text-pp-text">
                  Upgrade to{" "}
                  <span
                    className="font-semibold"
                    data-testid="your-tier-pending-target"
                  >
                    {pendingTierName}
                  </span>{" "}
                  · submitted{" "}
                  <span data-testid="your-tier-pending-time">
                    {fmtTimestamp(pendingRequest.created_at)}
                  </span>
                </span>
              </div>
            ) : upgradeOptions.length === 0 ? (
              <div
                className="flex items-center gap-2 rounded-2xl border border-border bg-muted px-4 py-3"
                data-testid="your-tier-top"
              >
                <Sparkles className="h-4 w-4 text-brand-orange" />
                <span className="text-sm text-pp-text">
                  You’re on our top tier — thanks for plucking with us.
                </span>
              </div>
            ) : (
              <Button
                type="button"
                onClick={() => setPickerOpen(true)}
                className="h-11 rounded-full bg-pp-orange px-6 text-pp-cta-text hover:bg-pp-orange-hover"
                data-testid="your-tier-upgrade-button"
              >
                Request upgrade
              </Button>
            )}
          </div>
        </>
      )}

      {/* Upgrade picker dialog */}
      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent
          className="max-w-md"
          data-testid="your-tier-picker-dialog"
        >
          <DialogHeader>
            <DialogTitle>Request a tier upgrade</DialogTitle>
            <DialogDescription>
              Pick a higher tier below. An admin will review your request.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            {upgradeOptions.map((t) => (
              <button
                key={t.id}
                type="button"
                disabled={submitting}
                onClick={() => submitUpgrade(t.id)}
                className="flex w-full items-center justify-between rounded-2xl border border-border bg-background px-4 py-3 text-left hover:border-pp-orange focus:outline-none focus:ring-2 focus:ring-pp-orange disabled:opacity-50"
                data-testid={`your-tier-option-${t.id}`}
              >
                <span>
                  <span className="font-heading text-base font-semibold text-pp-text">
                    {t.name}
                  </span>
                  <span className="ml-2 text-sm text-muted-foreground">
                    {fmtPct(t.commission_pct)} commission
                    {fmtMonthly(t.monthly_price) !== null
                      ? ` · ${fmtMonthly(t.monthly_price)}`
                      : ""}
                  </span>
                </span>
                {submitting && (
                  <Loader2 className="ml-3 h-4 w-4 animate-spin text-muted-foreground" />
                )}
              </button>
            ))}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setPickerOpen(false)}
              disabled={submitting}
              data-testid="your-tier-picker-cancel"
            >
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
